import { lazy, Suspense, type ComponentType } from 'react';
import { currentVariant } from '../lib/variant';

/**
 * The routing gate for the two landing designs (see `src/client/lib/variant.ts`).
 *
 * `/`, `/owners` and `/providers` each have two implementations, `pages/plain`
 * for variant A and `pages/notice` for variant B. Everything else — thank-you,
 * legal, unsubscribe, not-found — is a single design and routes straight to its
 * page as before.
 *
 * Both sets are `lazy()` for the same reason /admin is: a visitor is assigned
 * one variant for the whole session and must never pay to download the other
 * one's layout. Splitting here keeps the frame and the forms in the entry
 * chunk and ships only the design the visitor actually sees.
 *
 * Each variant page renders its own `<Seo>` from `content/seo`, so the two
 * treatments of a route share one title, one description and one canonical.
 * Nothing about the route table, the sitemap or the per-page SEO changes here.
 */

const PlainHome = lazy(() => import('./plain/HomePage'));
const PlainOwners = lazy(() => import('./plain/OwnersPage'));
const PlainProviders = lazy(() => import('./plain/ProvidersPage'));

const NoticeHome = lazy(() => import('./notice/HomePage'));
const NoticeOwners = lazy(() => import('./notice/OwnersPage'));
const NoticeProviders = lazy(() => import('./notice/ProvidersPage'));

/**
 * The fallback is deliberately empty. The chunk is small and same-origin, and a
 * spinner or a "loading" line would be a brand-facing string standing in for
 * roughly one frame of waiting.
 */
function Variant({ plain, notice }: { plain: ComponentType; notice: ComponentType }) {
  const Page = currentVariant() === 'b' ? notice : plain;
  return (
    <Suspense fallback={null}>
      <Page />
    </Suspense>
  );
}

export function VariantHomePage() {
  return <Variant plain={PlainHome} notice={NoticeHome} />;
}

export function VariantOwnersPage() {
  return <Variant plain={PlainOwners} notice={NoticeOwners} />;
}

export function VariantProvidersPage() {
  return <Variant plain={PlainProviders} notice={NoticeProviders} />;
}
